// iter87.548 — Squares admin config (Phase 1: config + game attach +
// roster opt-in/confirm + lock pool). iter87.551 added Finalize/
// Reverse Finalize (Phase 3 payout ledger). iter87.554 — no longer a
// standalone admin tab; rendered directly inside TrophyTokensTab for
// the 4 Squares templates (Trophy Tokens is now the one admin surface
// for everything Squares — the real game itself is picked earlier, in
// the Tournament Setup Wizard).
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Grid3x3, Save, Search, Lock, Info, AlertTriangle } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { useBrutalConfirm } from "@/components/BrutalConfirm";

const DEFAULT_CONFIG = {
    enabled: true,
    allocation_basis: "per_square",
    amount: 10,
    withhold_pct: 0,
    payout_type: "checkpoints",
    checkpoint_weighting: "weighted_40",
    unclaimed_square_policy: "redistribute_other_winners",
    max_squares_per_player: 20,
};

const WEIGHT_OPTIONS = [
    { key: "even", label: "Even", desc: "25% each checkpoint (Q1/Q2/Q3/Final)" },
    { key: "weighted_40", label: "40% Final", desc: "20/20/20/40" },
    { key: "weighted_55", label: "55% Final", desc: "15/15/15/55" },
    { key: "weighted_70", label: "70% Final", desc: "10/10/10/70" },
];

const UNCLAIMED_OPTIONS = [
    { key: "house_cut", label: "House cut" },
    { key: "final_pot", label: "Final pot" },
    { key: "evenly_all_confirmed", label: "Split evenly" },
    { key: "redistribute_other_winners", label: "Redistribute" },
];

function Pill({ active, disabled, onClick, children, testId }) {
    return (
        <button
            type="button"
            disabled={disabled}
            onClick={onClick}
            data-testid={testId}
            className={`text-xs font-bold uppercase border transition px-3 py-1.5 rounded-md ${
                active ? "border-volt/70 bg-volt/20 text-volt" : "border-zinc-600 bg-zinc-800 text-zinc-400 hover:border-volt/40 hover:text-volt"
            } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
        >
            {children}
        </button>
    );
}

export default function SquaresTokensTab({ tournament, league, onChanged }) {
    const tid = tournament?.id;
    const [config, setConfig] = useState(DEFAULT_CONFIG);
    const [mode, setMode] = useState("manual");
    const [sport, setSport] = useState("nfl");
    const [game, setGame] = useState(null);
    const [proPlusOk, setProPlusOk] = useState(true);
    const [locked, setLocked] = useState(false);
    const [poolLocked, setPoolLocked] = useState(false);
    const [poolLockedAt, setPoolLockedAt] = useState(null);
    const [roster, setRoster] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [lockingPool, setLockingPool] = useState(false);
    const [checkpointsData, setCheckpointsData] = useState(null);
    const [finalizing, setFinalizing] = useState(false);
    const [unfinalizing, setUnfinalizing] = useState(false);
    const [gameResults, setGameResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [attachingId, setAttachingId] = useState(null);
    const [showChangeGame, setShowChangeGame] = useState(false);
    const { confirm: brutalConfirm, hostElement: confirmHost } = useBrutalConfirm();

    const reload = useCallback(async () => {
        if (!tid) return;
        setLoading(true);
        try {
            const [cfgResp, rosterResp, checkpointsResp] = await Promise.all([
                api.squaresGetConfig(tid),
                api.squaresGetRoster(tid),
                api.squaresGetCheckpoints(tid),
            ]);
            setConfig({ ...DEFAULT_CONFIG, ...(cfgResp.config || {}) });
            setMode(cfgResp.mode || "manual");
            setSport(cfgResp.sport || "nfl");
            setGame(cfgResp.game || null);
            setProPlusOk(cfgResp.pro_plus_ok !== false);
            setLocked(!!cfgResp.locked);
            setPoolLocked(!!cfgResp.pool_locked);
            setPoolLockedAt(cfgResp.pool_locked_at || null);
            setRoster(rosterResp.roster || []);
            setCheckpointsData(checkpointsResp);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to load Squares");
        }
        setLoading(false);
    }, [tid]);

    useEffect(() => { reload(); }, [reload]);

    const saveConfig = async () => {
        setSaving(true);
        try {
            await api.squaresPutConfig(tid, config);
            toast.success("Squares config saved");
            onChanged?.();
            await reload();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Save failed");
        }
        setSaving(false);
    };

    const loadCuratedGames = useCallback(async () => {
        if (!tid) return;
        setSearching(true);
        try {
            const resp = await api.squaresSearchGames(tid);
            setGameResults(resp.games || []);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't load the curated game list");
        }
        setSearching(false);
    }, [tid]);

    // iter87.553 — curated list auto-loads once a board has no game
    // attached yet; no more free-text search to trigger it.
    useEffect(() => {
        if (!loading && !game && !locked) loadCuratedGames();
    }, [loading, game, locked, loadCuratedGames]);

    const attachGame = async (g) => {
        setAttachingId(g.vendor_game_id);
        try {
            await api.squaresPutGame(tid, g.vendor_game_id);
            toast.success(`Attached ${g.away_name} @ ${g.home_name}`);
            setGameResults([]);
            setShowChangeGame(false);
            onChanged?.();
            await reload();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Attach failed");
        }
        setAttachingId(null);
    };

    const setOptState = async (pid, targetState) => {
        if (locked) return;
        try {
            if (targetState === "confirmed") {
                await api.squaresConfirm(tid, pid);
            } else if (targetState === "opted_in") {
                await api.squaresSetOptIn(tid, pid, true);
            } else {
                await api.squaresSetOptIn(tid, pid, false);
            }
            onChanged?.();
            await reload();
        } catch (err) {
            toast.error(err.response?.data?.detail || "State change failed");
        }
    };

    const doLockPool = async () => {
        const ok = await brutalConfirm({
            title: "Lock the Squares pool now?",
            body: "This permanently freezes config, the attached game, and the roster. Unconfirmed opt-ins get dropped. This cannot be undone.",
            confirmLabel: "Lock pool",
            destructive: true,
        });
        if (!ok) return;
        setLockingPool(true);
        try {
            const resp = await api.squaresLockPool(tid);
            toast.success(
                resp.dropped_count > 0
                    ? `Pool locked · dropped ${resp.dropped_count} unconfirmed`
                    : "Pool locked",
            );
            onChanged?.();
            await reload();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Lock pool failed");
        }
        setLockingPool(false);
    };

    // iter87.551 — Finalize/Reverse Finalize mirror TrophyTokensTab's
    // one-tap pattern exactly: admin-triggered, idempotent, gated on
    // the attached game actually being final (FT/AOT).
    const doFinalize = async () => {
        const ok = await brutalConfirm({
            title: "Finalize Squares payouts now?",
            body: "This credits tokens to each checkpoint's winning box owner based on the final vendor score. Can be undone with Reverse Finalize afterward — don't finalize until the game result is final.",
            confirmLabel: "Finalize payouts",
            destructive: true,
        });
        if (!ok) return;
        setFinalizing(true);
        try {
            const resp = await api.squaresFinalize(tid);
            const total = (resp.ledger || []).reduce((s, a) => s + a.tokens_awarded, 0);
            toast.success(resp.idempotent_replay ? "Already finalized" : `Finalized · ${total} tokens awarded across ${resp.ledger.length} row(s)`);
            onChanged?.();
            await reload();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Finalize failed");
        }
        setFinalizing(false);
    };

    const doUnfinalize = async () => {
        const ok = await brutalConfirm({
            title: "Reverse the Squares finalize?",
            body: "This drops every awarded ledger row on this tournament and lets you re-run finalize later. Anyone who won tokens will lose them until a re-finalize.",
            confirmLabel: "Reverse finalize",
            destructive: true,
        });
        if (!ok) return;
        setUnfinalizing(true);
        try {
            const resp = await api.squaresUnfinalize(tid);
            toast.success(resp.dropped_rows > 0 ? `Reversed · dropped ${resp.dropped_rows} ledger row${resp.dropped_rows === 1 ? "" : "s"}` : "Reversed · no ledger rows found");
            onChanged?.();
            await reload();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Reverse finalize failed");
        }
        setUnfinalizing(false);
    };

    if (loading) {
        return <div className="text-center text-zinc-500 text-sm py-8" data-testid="squares-loading">Loading Squares…</div>;
    }

    if (!proPlusOk) {
        return (
            <div className="rounded-lg border border-blaze/40 bg-blaze/10 p-4 text-sm text-blaze flex items-start gap-2" data-testid="squares-pro-plus-gate">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                Squares is a Pro+ subscriber feature. Upgrade this rivalry to unlock it.
            </div>
        );
    }

    return (
        <div className="space-y-5" data-testid="squares-tokens-tab">
            {confirmHost}
            <div className="flex items-center gap-2 text-xs text-zinc-400">
                <Grid3x3 className="w-4 h-4 text-volt" />
                <span className="font-bold uppercase tracking-wide">
                    {sport === "nfl" ? "NFL" : "CFB"} Squares — {mode === "auto" ? "Luck of the Draw" : "Pick Your Boxes"}
                </span>
            </div>

            {locked && (
                <div className="rounded-lg border border-cyber/40 bg-cyber/10 p-3 flex items-start gap-2" data-testid="squares-pool-locked-banner">
                    <Lock className="w-3.5 h-3.5 text-cyber shrink-0 mt-0.5" />
                    <div className="text-[11px] text-cyber leading-relaxed">
                        {poolLocked ? `Pool locked${poolLockedAt ? ` (${new Date(poolLockedAt).toLocaleString()})` : ""}` : "Locked (kickoff passed)"} — config, game, and roster are frozen.
                    </div>
                </div>
            )}

            {/* ---- Game attach ---- */}
            <div className="brutal-card p-4 bg-ink border-white/10" data-testid="squares-game-card">
                <div className="text-xs font-bold uppercase tracking-wide text-zinc-300 mb-2">The Game</div>
                {game ? (
                    <div className="rounded-lg border border-volt/40 bg-volt/[0.06] p-3 text-sm" data-testid="squares-attached-game">
                        <div className="flex items-start justify-between gap-2">
                            <div>
                                <div className="font-bold text-white">{game.away_name} @ {game.home_name}</div>
                                <div className="text-[11px] text-zinc-500 mt-1">
                                    Kickoff: {game.scheduled_at ? new Date(game.scheduled_at).toLocaleString() : "TBD"}
                                </div>
                            </div>
                            {!locked && (
                                <button
                                    type="button"
                                    onClick={() => { setShowChangeGame((v) => !v); if (!showChangeGame) loadCuratedGames(); }}
                                    data-testid="squares-change-game-btn"
                                    className="brutal-btn-ghost px-2.5 py-1.5 text-[10px] font-bold uppercase shrink-0"
                                >
                                    {showChangeGame ? "Cancel" : "Change game"}
                                </button>
                            )}
                        </div>
                    </div>
                ) : (
                    <p className="text-[11px] text-zinc-500 mb-2" data-testid="squares-no-game-hint">
                        No game attached yet — pick from the curated {sport === "nfl" ? "NFL" : "CFB"} playoff shortlist below.
                    </p>
                )}
                {!locked && (!game || showChangeGame) && (
                    <div className="mt-3 space-y-2">
                        <div className="flex items-center justify-between">
                            <span className="text-[10px] uppercase tracking-wide text-zinc-500 font-bold">
                                Curated shortlist — no free-text search
                            </span>
                            <button
                                type="button"
                                onClick={loadCuratedGames}
                                disabled={searching}
                                data-testid="squares-game-refresh-btn"
                                className="brutal-btn-ghost px-2.5 py-1.5 text-[10px] font-bold uppercase flex items-center gap-1.5"
                            >
                                <Search className="w-3 h-3" /> {searching ? "Loading…" : "Refresh"}
                            </button>
                        </div>
                        {searching && gameResults.length === 0 && (
                            <p className="text-[11px] text-zinc-600" data-testid="squares-game-loading">Checking the vendor feed…</p>
                        )}
                        {!searching && gameResults.length === 0 && (
                            <p className="text-[11px] text-zinc-600" data-testid="squares-game-empty">
                                Nothing on the shortlist yet — the vendor publishes {sport === "nfl" ? "Super Bowl / Conference Championship" : "CFP bowl / National Championship"} matchups closer to kickoff. Check back later.
                            </p>
                        )}
                        {gameResults.length > 0 && (
                            <div className="max-h-64 overflow-y-auto space-y-1.5" data-testid="squares-game-results">
                                {gameResults.map((g) => (
                                    <button
                                        key={g.vendor_game_id}
                                        type="button"
                                        onClick={() => attachGame(g)}
                                        disabled={attachingId === g.vendor_game_id}
                                        data-testid={`squares-game-result-${g.vendor_game_id}`}
                                        className="w-full text-left rounded-lg border border-zinc-700 bg-zinc-900 hover:border-volt/50 p-2.5 text-xs transition"
                                    >
                                        <div className="text-volt font-bold uppercase tracking-wide text-[10px] mb-0.5">{g.round_label || g.week}</div>
                                        <div className="font-bold text-white">{g.away_name} @ {g.home_name}</div>
                                        <div className="text-zinc-500 mt-0.5">
                                            {g.scheduled_at ? new Date(g.scheduled_at).toLocaleString() : "Date TBD"}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* ---- Config ---- */}
            <div className="brutal-card p-4 bg-ink border-white/10 space-y-4" data-testid="squares-config-card">
                <div className="text-xs font-bold uppercase tracking-wide text-zinc-300">Payout Setup</div>

                <div>
                    <div className="text-[10px] uppercase tracking-wide text-zinc-500 mb-1.5">Token cost basis</div>
                    <div className="flex gap-2">
                        <Pill active={config.allocation_basis === "per_square"} disabled={locked} testId="squares-basis-per-square"
                            onClick={() => setConfig(c => ({ ...c, allocation_basis: "per_square" }))}>Per Box</Pill>
                        <Pill active={config.allocation_basis === "per_player"} disabled={locked} testId="squares-basis-per-player"
                            onClick={() => setConfig(c => ({ ...c, allocation_basis: "per_player" }))}>Per Player</Pill>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="text-[10px] uppercase tracking-wide text-zinc-500 block mb-1.5">Amount (tokens)</label>
                        <input type="number" min={1} max={100000} value={config.amount} disabled={locked}
                            onChange={(e) => setConfig(c => ({ ...c, amount: parseInt(e.target.value, 10) || 1 }))}
                            data-testid="squares-amount-input"
                            className="w-full rounded-xl bg-[#0e0e0e] border border-white/[0.10] focus:border-volt/60 focus:outline-none px-3 py-2 text-sm text-white" />
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-wide text-zinc-500 block mb-1.5">House cut (%)</label>
                        <input type="number" min={0} max={75} value={config.withhold_pct} disabled={locked}
                            onChange={(e) => setConfig(c => ({ ...c, withhold_pct: parseInt(e.target.value, 10) || 0 }))}
                            data-testid="squares-withhold-input"
                            className="w-full rounded-xl bg-[#0e0e0e] border border-white/[0.10] focus:border-volt/60 focus:outline-none px-3 py-2 text-sm text-white" />
                    </div>
                </div>

                {mode === "manual" && (
                    <div>
                        <label className="text-[10px] uppercase tracking-wide text-zinc-500 block mb-1.5">Max boxes per player (0 = unlimited)</label>
                        <input type="number" min={0} max={100} value={config.max_squares_per_player} disabled={locked}
                            onChange={(e) => setConfig(c => ({ ...c, max_squares_per_player: parseInt(e.target.value, 10) || 0 }))}
                            data-testid="squares-cap-input"
                            className="w-full rounded-xl bg-[#0e0e0e] border border-white/[0.10] focus:border-volt/60 focus:outline-none px-3 py-2 text-sm text-white" />
                        <p className="text-[10px] text-zinc-600 mt-1">Pick'em Squares only — Auto-Draw deals boxes evenly, no cap needed.</p>
                    </div>
                )}

                <div>
                    <div className="text-[10px] uppercase tracking-wide text-zinc-500 mb-1.5">Game type</div>
                    <div className="flex gap-2">
                        <Pill active={config.payout_type === "wta"} disabled={locked} testId="squares-payout-wta"
                            onClick={() => setConfig(c => ({
                                ...c, payout_type: "wta",
                                unclaimed_square_policy: c.unclaimed_square_policy === "redistribute_other_winners"
                                    ? "final_pot" : c.unclaimed_square_policy,
                            }))}>Final Score Only</Pill>
                        <Pill active={config.payout_type === "checkpoints"} disabled={locked} testId="squares-payout-checkpoints"
                            onClick={() => setConfig(c => ({ ...c, payout_type: "checkpoints" }))}>Quarterly Checkpoints</Pill>
                    </div>
                </div>

                {config.payout_type === "checkpoints" && (
                    <div>
                        <div className="text-[10px] uppercase tracking-wide text-zinc-500 mb-1.5">Checkpoint weighting</div>
                        <div className="flex flex-wrap gap-2" data-testid="squares-weighting-options">
                            {WEIGHT_OPTIONS.map((w) => (
                                <Pill key={w.key} active={config.checkpoint_weighting === w.key} disabled={locked}
                                    testId={`squares-weighting-${w.key}`}
                                    onClick={() => setConfig(c => ({ ...c, checkpoint_weighting: w.key }))}>
                                    {w.label}
                                </Pill>
                            ))}
                        </div>
                    </div>
                )}

                <div>
                    <div className="flex items-center gap-1.5 mb-1.5">
                        <div className="text-[10px] uppercase tracking-wide text-zinc-500">If the winning score lands on an unclaimed box</div>
                        <Popover>
                            <PopoverTrigger asChild>
                                <button type="button" data-testid="squares-unclaimed-info-btn"><Info className="w-3 h-3 text-zinc-500" /></button>
                            </PopoverTrigger>
                            <PopoverContent className="max-w-xs text-xs space-y-1.5">
                                <p><b>House cut:</b> that checkpoint's share rolls into the house cut.</p>
                                <p><b>Final pot:</b> that checkpoint's share rolls into the Final payout.</p>
                                <p><b>Split evenly:</b> that checkpoint's share is divided across every confirmed player, win or lose.</p>
                                <p><b>Redistribute:</b> that checkpoint's share is divided across whoever DID win a box at other checkpoints.</p>
                            </PopoverContent>
                        </Popover>
                    </div>
                    <div className="flex flex-wrap gap-2" data-testid="squares-unclaimed-options">
                        {UNCLAIMED_OPTIONS.map((o) => {
                            const isRedistribute = o.key === "redistribute_other_winners";
                            const disabledOpt = locked || (isRedistribute && config.payout_type === "wta");
                            return (
                                <Pill key={o.key} active={config.unclaimed_square_policy === o.key} disabled={disabledOpt}
                                    testId={`squares-unclaimed-${o.key}`}
                                    onClick={() => setConfig(c => ({ ...c, unclaimed_square_policy: o.key }))}>
                                    {o.label}
                                </Pill>
                            );
                        })}
                    </div>
                </div>

                <button
                    type="button"
                    onClick={saveConfig}
                    disabled={saving || locked}
                    data-testid="squares-save-config-btn"
                    className="brutal-btn-primary w-full py-2.5 text-sm font-bold uppercase flex items-center justify-center gap-2 disabled:opacity-50"
                >
                    <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save Config"}
                </button>
            </div>

            {/* ---- Roster ---- */}
            <div className="brutal-card p-4 bg-ink border-white/10" data-testid="squares-roster-card">
                <div className="text-xs font-bold uppercase tracking-wide text-zinc-300 mb-2">Roster</div>
                <p className="text-[11px] text-zinc-500 leading-relaxed mb-2">
                    {locked
                        ? "Locked — no further opt-in/confirm moves."
                        : "Two-step: Opt In, then Confirm. Only Confirmed crewmates can claim boxes or receive an auto-assignment."}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2" data-testid="squares-roster">
                    {roster.map((r) => {
                        const state = r.state || "none";
                        const canOptIn = !locked;
                        const canConfirm = !locked;
                        const stateLabel = ({
                            none: "Not opted in", opted_in: "Opted in · awaiting confirm",
                            confirmed: "Confirmed", dropped: "Dropped",
                        }[state] || state);
                        return (
                            <div
                                key={r.id}
                                data-testid={`squares-roster-row-${r.id}`}
                                data-state={state}
                                className={`rounded-lg border p-2.5 transition ${
                                    state === "confirmed" ? "border-volt/50 bg-volt/[0.06]"
                                    : state === "opted_in" ? "border-heat/50 bg-heat/[0.06]"
                                    : state === "dropped" ? "border-zinc-800 bg-zinc-950 text-zinc-600 opacity-60"
                                    : "border-zinc-700 bg-zinc-900 text-zinc-400"
                                }`}
                            >
                                <div className="font-bold text-xs truncate">{r.username}</div>
                                <div className="text-[10px] opacity-80 mt-0.5 mb-1.5">{stateLabel}</div>
                                <div className="flex items-center gap-1">
                                    <button type="button" disabled={!canOptIn || state === "opted_in"} onClick={() => setOptState(r.id, "opted_in")}
                                        data-testid={`squares-optin-btn-${r.id}`}
                                        className="flex-1 pill text-[9px] font-bold uppercase border border-zinc-600 bg-zinc-800 text-zinc-400 hover:border-heat/50 hover:text-heat disabled:opacity-50">
                                        Opt In
                                    </button>
                                    <button type="button" disabled={!canConfirm || state === "confirmed"} onClick={() => setOptState(r.id, "confirmed")}
                                        data-testid={`squares-confirm-btn-${r.id}`}
                                        className="flex-1 pill text-[9px] font-bold uppercase border border-zinc-600 bg-zinc-800 text-zinc-400 hover:border-volt/50 hover:text-volt disabled:opacity-50">
                                        Confirm
                                    </button>
                                    <button type="button" disabled={!canOptIn || state === "dropped"} onClick={() => setOptState(r.id, "dropped")}
                                        data-testid={`squares-drop-btn-${r.id}`}
                                        className="flex-1 pill text-[9px] font-bold uppercase border border-zinc-600 bg-zinc-800 text-zinc-400 hover:border-blaze/50 hover:text-blaze disabled:opacity-50">
                                        Drop
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                    {roster.length === 0 && <p className="text-xs text-zinc-600 col-span-full">No crewmates on this rivalry yet.</p>}
                </div>
            </div>

            {/* ---- Lock Pool ---- */}
            {!locked && (
                <button
                    type="button"
                    onClick={doLockPool}
                    disabled={lockingPool || !config.enabled}
                    data-testid="squares-lock-pool-btn"
                    className="brutal-btn-ghost w-full py-2.5 text-sm font-bold uppercase flex items-center justify-center gap-2 border-blaze/40 text-blaze disabled:opacity-50"
                >
                    <Lock className="w-4 h-4" /> {lockingPool ? "Locking…" : "Lock Pool Early"}
                </button>
            )}

            {/* ---- Finalize (iter87.551) ---- */}
            {locked && (
                <div className="rounded-lg border border-white/10 bg-ink p-3 space-y-2" data-testid="squares-finalize-section">
                    <div className="flex items-center justify-between gap-2">
                        <span className="text-[10px] uppercase tracking-wide text-zinc-500">Finalize payouts</span>
                        {checkpointsData?.finalized ? (
                            <span className="text-[10px] font-bold text-volt uppercase" data-testid="squares-finalized-badge">Finalized</span>
                        ) : checkpointsData?.game_finished ? (
                            <span className="text-[10px] font-bold text-heat uppercase">Game final — ready</span>
                        ) : (
                            <span className="text-[10px] text-zinc-400 uppercase">Waiting for game to end</span>
                        )}
                    </div>
                    {!checkpointsData?.finalized ? (
                        <button
                            type="button"
                            onClick={doFinalize}
                            disabled={finalizing || !checkpointsData?.game_finished}
                            data-testid="squares-finalize-btn"
                            className="brutal-btn-ghost w-full py-2.5 text-sm font-bold uppercase flex items-center justify-center gap-2 border-volt/40 text-volt disabled:opacity-50"
                        >
                            {finalizing ? "Finalizing…" : "Finalize Payouts"}
                        </button>
                    ) : (
                        <button
                            type="button"
                            onClick={doUnfinalize}
                            disabled={unfinalizing}
                            data-testid="squares-unfinalize-btn"
                            className="brutal-btn-ghost w-full py-2.5 text-sm font-bold uppercase flex items-center justify-center gap-2 border-blaze/40 text-blaze disabled:opacity-50"
                        >
                            {unfinalizing ? "Reversing…" : "Reverse Finalize"}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}
